import { GlobalStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import _ from 'lodash';
import { InsuranceAgentDirectoryService } from '../services/insuranceAgentDirectoryService';
import { InsuranceAgentRecord, matchAgents } from '../utils/insuranceAgentDirectoryUtils';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

interface InsuranceAgentDirectoryDto {
  insuranceAgentId: number;
  lenderId: number;
  agentName: string;
  agentEmail: string;
  agentPhone: string;
  addressLine: string;
  city: string;
  state: string;
  zipCode: string;
}

const toInsuranceAgentRecord = (
  agent: InsuranceAgentDirectoryDto
): InsuranceAgentRecord => ({
  insuranceAgentId: agent.insuranceAgentId,
  name: agent.agentName,
  email: agent.agentEmail,
  phone: agent.agentPhone,
  address: [agent.addressLine, agent.city, [agent.state, agent.zipCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', '),
});

export class InsuranceAgentDirectoryStore {
  private globalStore: GlobalStore;
  private insuranceAgentDirectoryService: InsuranceAgentDirectoryService;
  private searchDebounced: (query: string) => void;

  private agents: InsuranceAgentRecord[];
  private agentsPromise: Promise<InsuranceAgentRecord[]> | null;

  query!: string;
  results!: InsuranceAgentRecord[];
  isOpen!: boolean;
  isLoading!: boolean;
  selected!: InsuranceAgentRecord | null;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.insuranceAgentDirectoryService = new InsuranceAgentDirectoryService();
    this.agents = [];
    this.agentsPromise = null;
    this.setDefaults();

    makeObservable(this, {
      query: observable,
      results: observable,
      isOpen: observable,
      isLoading: observable,
      selected: observable,
      matchCount: computed,
      showsScrollHint: computed,
      isEmpty: computed,
      setDefaults: action,
      reset: action,
      seedQuery: action,
      setQuery: action,
      openDropdown: action,
      closeDropdown: action,
      selectAgent: action,
      clearSelection: action,
    });

    this.searchDebounced = _.debounce(
      (query: string) => this.search(query),
      SEARCH_DEBOUNCE_MS
    );
  }

  setDefaults() {
    this.query = '';
    this.results = [];
    this.isOpen = false;
    this.isLoading = false;
    this.selected = null;
  }

  reset() {
    this.setDefaults();
  }

  get matchCount() {
    return this.results.length;
  }

  get showsScrollHint() {
    return this.results.length > 4;
  }

  get isEmpty() {
    return (
      !this.isLoading &&
      this.query.trim().length >= MIN_QUERY_LENGTH &&
      this.results.length === 0
    );
  }

  seedQuery(query: string) {
    this.query = query || '';
  }

  setQuery(query: string) {
    this.query = query;
    this.selected = null;
    if (query.trim().length < MIN_QUERY_LENGTH) {
      this.results = [];
      this.isOpen = false;
      return;
    }
    this.isOpen = true;
    this.searchDebounced(query);
  }

  private loadAgents(): Promise<InsuranceAgentRecord[]> {
    if (!this.agentsPromise) {
      this.agentsPromise = this.insuranceAgentDirectoryService
        .getInsuranceAgentsDirectory()
        .then(response => {
          const agents: InsuranceAgentDirectoryDto[] = response?.data?.data ?? [];
          this.agents = agents.map(toInsuranceAgentRecord);
          return this.agents;
        })
        .catch(error => {
          // Don't cache a failed fetch — let the next search retry.
          this.agentsPromise = null;
          throw error;
        });
    }
    return this.agentsPromise;
  }

  private search = flow(function* (this: InsuranceAgentDirectoryStore, query: string) {
    this.isLoading = true;
    try {
      const agents = yield this.loadAgents();
      this.results = matchAgents(agents, query.trim());
    } catch (e) {
      this.results = [];
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error searching the insurance agent directory.',
      });
    } finally {
      this.isLoading = false;
    }
  });

  openDropdown() {
    if (this.query.trim().length >= MIN_QUERY_LENGTH) {
      this.isOpen = true;
    }
  }

  closeDropdown() {
    this.isOpen = false;
  }

  selectAgent(agent: InsuranceAgentRecord) {
    this.selected = agent;
    this.query = agent.email;
    this.isOpen = false;
  }

  clearSelection() {
    this.selected = null;
    this.query = '';
    this.results = [];
    this.isOpen = false;
  }

  invalidate() {
    this.agentsPromise = null;
  }
}

export default InsuranceAgentDirectoryStore;
